import { useMemo, useRef } from "react";
import type * as type from "@vTypes";
import type { NurseResponse } from "@/types/nurse-type";
import { OPTIONS as SHIFT_OPTIONS } from "@constants/SHIFT";
import {
  getDefaultShiftsSortedBySequence,
  ShiftMapper,
} from "@utils/shift-util.ts";
import { useIsAdmin } from "@hooks/useAuth.ts";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import useRosterConfig from "@/hooks/useRosterConfig";
import { cn } from "@/utils/cn";

function WeeklyOffTableRow({
  nurse,
  index,
  assignedDay,
  showDayColumn = false,
  isHighlighted = false,
  isSelected = false,
  getPreceptorInfo,
  // 총 지정 인원 테이블만의 스타일 적용을 위함.
  isTotalView = false,
  selectedGroupId,
}: {
  nurse: type.FullNurseSchema;
  index: number;
  // 근무코드배열
  assignedDay?: string;
  showDayColumn?: boolean;
  isHighlighted?: boolean;
  isSelected?: boolean;
  getPreceptorInfo?: (nurse: type.FullNurseSchema) => string;
  isTotalView?: boolean;
  selectedGroupId: string | null;
}) {
  // 하이라이트된 행의 텍스트 색상 클래스
  const textColorClass = isHighlighted ? "text-blue-600 font-bold" : "";

  const rowRef = useRef<HTMLDivElement | null>(null);
  function filterDate(date: Date) {
    // DB 저장형태 : 2000-00-00T00:00:00
    return date ? String(date).split("T")[0] : "";
  }
  // 프리셉터 정보 표시
  const preceptorInfo = getPreceptorInfo
    ? getPreceptorInfo(nurse)
    : nurse.preceptor_id || "-";

  const isAdmin = useIsAdmin();
  const { data: shiftsRes } = useShiftsQuery(
    isAdmin ? (selectedGroupId ?? "") : ""
  );
  const { data: serverConfig } = useRosterConfig();
  const isUseMid = serverConfig?.use_mid;

  // Shift 인스턴스 생성 (shift_id 키 — 고정근무 색상 등에 사용)
  const shiftMapper = useMemo(() => {
    if (!shiftsRes) return null;
    return new ShiftMapper(shiftsRes);
  }, [shiftsRes]);

  /** 근무자 관리(NurseTableRow)와 동일: 저장값은 default_shift, 표시는 shift_id */
  const shiftOptions = useMemo<Array<{ label: string; value: string }>>(() => {
    if (!shiftsRes || shiftsRes.length === 0) {
      const base = SHIFT_OPTIONS.slice(1).map((o) => ({
        label: (o as { short: string }).short,
        value: (o as { short: string }).short,
      }));
      return isUseMid !== false ? [...base, { label: "M", value: "M" }] : base;
    }

    const deduped = new Map<string, { label: string; value: string }>();
    getDefaultShiftsSortedBySequence(shiftsRes, { excludeOff: true }).forEach(
      (shift) => {
        const code = shift.default_shift;
        if (!code) return;
        if (isUseMid === false && code === "M") return;
        if (!deduped.has(code)) {
          deduped.set(code, { label: shift.shift_id || code, value: code });
        }
      }
    );

    return Array.from(deduped.values());
  }, [shiftsRes, isUseMid]);

  const fixedShiftOptions = useMemo<
    Array<{ label: string; value: string; color: string }>
  >(() => {
    if (!shiftsRes) return [];
    const list = shiftsRes
      .filter((shift: { shift_gb?: string }) => shift.shift_gb === "고정")
      .map((shift: { name: string; shift_id: string; color: string }) => ({
        label: shift.name,
        value: shift.shift_id,
        color: shift.color,
      }));
    if (isUseMid === false) {
      return list.filter((o) => o.value !== "M");
    }
    return list;
  }, [shiftsRes, isUseMid]);

  const defaultShiftColorMap = useMemo(() => {
    const map = new Map<string, string>();
    (shiftsRes ?? []).forEach((shift: { default_shift?: string; color: string }) => {
      if (shift.default_shift && !map.has(shift.default_shift)) {
        map.set(shift.default_shift, shift.color);
      }
    });
    return map;
  }, [shiftsRes]);

  const fixedShift = (nurse as NurseResponse).fixed_shift ?? null;

  if (!nurse) {
    <div>없어요</div>;
  }

  return (
    <div
      ref={rowRef}
      data-nid={nurse.nurse_id}
      className={`${isTotalView ? "cursor-default" : "cursor-grab"} comp_tr comp_tr--hover ${showDayColumn ? "comp_tr--mgmt2-with-day" : "comp_tr--mgmt2"} ${
        isSelected ? "selected-nurse" : ""
      }`}
    >
      <div className="flex justify-center items-center text-[15px] text-center">
        {/* 드래그 하기위한 클래스명 comp_btn_dragHandler */}
        <div
          className={`comp_btn_dragHandler ${isTotalView && "!cursor-default"}`}
        >
          <span className="img_iconHandler"></span>
        </div>
        {/* <div className="w-full h-full flex justify-center items-center cursor-grab"><span className="img_iconHandler"></span></div> */}
      </div>
      {/* No */}
      <div
        className={`flex justify-center items-center text-[15px] text-center ${textColorClass}`}
      >
        {index + 1}
      </div>
      {/* 사번 */}
      <div
        className={`flex justify-center items-center text-[15px] text-center ${textColorClass}`}
      >
        {nurse.emp_num ? nurse.emp_num : "-"}
      </div>

      {/* 계정 아이디 제거*/}
      {/* <div
                className={`flex justify-center items-center text-[15px] text-center ${textColorClass}`}
            >
                {nurse.account_id}
            </div> */}

      {/* 이름 */}
      <div className="flex justify-center items-center text-[15px] text-center">
        <span
          className={
            isHighlighted
              ? "bg-black text-white px-2 py-1 rounded font-bold inline-block"
              : ""
          }
        >
          {nurse.name}
        </span>
      </div>

      {/* 직군 */}
      <div
        className={`flex justify-center items-center text-[15px] text-center ${textColorClass}`}
      >
        {nurse.role}
      </div>

      {/* 경력 */}
      <div
        className={`flex justify-center items-center text-[15px] text-center ${textColorClass}`}
      >
        {nurse.experience}
      </div>

      {/* 근무형태 — 근무자 관리(NurseTableRow) 읽기 전용과 동일: shift_id 표기 + default_shift 기준 색상 */}
      <div
        className={cn(
          "flex justify-center items-center text-[15px] text-center gap-0.5 flex-wrap"
        )}
      >
        <div className="w-full flex justify-center items-center gap-0.5">
          {fixedShift ? (
            <span
              className={cn("text-sm font-medium text-white p-1.5 px-2 rounded-sm")}
              style={{
                backgroundColor:
                  shiftMapper?.getColor(fixedShift) ?? "#000000",
              }}
            >
              <span className="font-bold">
                {fixedShiftOptions.find((o) => o.value === fixedShift)
                  ?.value ?? fixedShift}
              </span>
              <span className="ml-1">고정</span>
            </span>
          ) : (
            (() => {
              const nightNurseShifts = nurse.is_night_nurse || [];

              if (nightNurseShifts.length === 0) {
                const allShifts = shiftOptions.filter((opt) => {
                  if (isUseMid === false && opt.value === "M") return false;
                  return true;
                });

                if (allShifts.length === 0) {
                  return <span className="text-sm opacity-40">-</span>;
                }

                return allShifts.map((opt, i) => {
                  const color =
                    defaultShiftColorMap.get(opt.value) ||
                    shiftMapper?.getColor(opt.value) ||
                    "#000000";
                  return (
                    <span
                      key={i}
                      className="font-bold size-6.5 text-white flex items-center justify-center shrink-0 rounded-[5px] text-xs"
                      style={{ backgroundColor: color }}
                    >
                      {opt.label}
                    </span>
                  );
                });
              }

              const shiftsToDisplay = nightNurseShifts
                .map((shiftCode) => {
                  const opt = shiftOptions.find((o) => o.value === shiftCode);
                  if (!opt) return null;
                  if (isUseMid === false && shiftCode === "M") return null;
                  return opt;
                })
                .filter(
                  (opt): opt is { label: string; value: string } => opt !== null
                );

              if (shiftsToDisplay.length === 0) {
                return <span className="text-sm opacity-40">-</span>;
              }

              return shiftsToDisplay.map((opt, i) => {
                const color =
                  defaultShiftColorMap.get(opt.value) ||
                  shiftMapper?.getColor(opt.value) ||
                  "#000000";
                return (
                  <span
                    key={i}
                    className="font-bold size-6.5 text-white flex items-center justify-center shrink-0 rounded-[5px] text-xs"
                    style={{ backgroundColor: color }}
                  >
                    {opt.label}
                  </span>
                );
              });
            })()
          )}
        </div>
      </div>

      {/* 프리셉터 */}
      <div
        className={`flex justify-center items-center text-[15px] text-center ${textColorClass} ${preceptorInfo === "없음" && "notSelected"}`}
      >
        {preceptorInfo}
      </div>

      {/* 입사일 */}
      <div
        className={`flex justify-center items-center text-[15px] text-center ${textColorClass}`}
      >
        {/* TODO : 날짜라이브러리 적용 필요 */}
        <div className="comp_inputDate comp_inputDate--div">
          {nurse.joining_date ? filterDate(nurse.joining_date) : "-"}
        </div>
      </div>

      {/* 적용 해제일 (퇴사일) */}
      <div
        className={`flex justify-center items-center text-[15px] text-center ${textColorClass}`}
      >
        {/* TODO : 날짜라이브러리 적용 필요 */}
        <div className="comp_inputDate comp_inputDate--div">
          {nurse.resignation_date ? filterDate(nurse.resignation_date) : "-"}
        </div>
      </div>

      {/* 메모 */}
      <div className="flex items-center text-[15px] text-center text-sm text-[#939399] justify-start">
        <div className="w-full py-[3px] px-2 border border-transparent leading-[1.4em] rounded-[5px]">
          {nurse.nurse_memo ? nurse.nurse_memo : "-"}
        </div>
      </div>

      {/* 요일 (총 배치인원 탭에서만 표시) */}
      {assignedDay && (
        <div
          className={`flex justify-center items-center text-[15px] text-center ${textColorClass}`}
        >
          {assignedDay}
        </div>
      )}
    </div>
  );
}

export default WeeklyOffTableRow;
